import { createContext } from 'react';

/* =====================================================================
   colorMode.js — manual light/dark override layered on the OS setting
   ---------------------------------------------------------------------
   Three modes:
     'auto'  -> follow the operating system (prefers-color-scheme)
     'light' -> force light  (overrides the OS)
     'dark'  -> force dark   (overrides the OS)
   The choice is persisted to localStorage; a manual mode always wins over
   the OS. JS resolves the effective scheme and toggles `.dark-mode` on
   <html>; colors-dark.css keys its overrides off that class.

   NOTE: this is the LIGHT/DARK axis only. The BRAND theme
   (blueberry/jade/mexican) still comes solely from constants.js THEME and
   is untouched here.
   ===================================================================== */

export const COLOR_MODES = ['auto', 'light', 'dark'];
const STORAGE_KEY = 'fdwa_color_mode';

export const getStoredColorMode = () => {
  try {
    const m = localStorage.getItem(STORAGE_KEY);
    return COLOR_MODES.includes(m) ? m : 'auto';
  } catch (e) {
    return 'auto';
  }
};

export const setStoredColorMode = (mode) => {
  try {
    localStorage.setItem(STORAGE_KEY, mode);
  } catch (e) {
    /* storage disabled / private mode — ignore */
  }
};

export const osPrefersDark = () => {
  try {
    return typeof window !== 'undefined'
      && typeof window.matchMedia === 'function'
      && window.matchMedia('(prefers-color-scheme: dark)').matches;
  } catch (e) {
    return false;
  }
};

// Manual override wins over the OS; 'auto' follows the OS.
export const isDarkActive = (mode) =>
  mode === 'dark' || (mode === 'auto' && osPrefersDark());

// Toggle the html.dark-mode class WITHOUT clobbering the brand theme class,
// and set color-scheme so native widgets (scrollbars, form controls, date
// pickers) render in the matching scheme.
export const applyDarkClass = (dark) => {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  root.classList.toggle('dark-mode', !!dark);
  root.style.colorScheme = dark ? 'dark' : 'light';
};

// Call at module load (from main.js, AFTER the brand theme class is applied) so
// the correct scheme is on <html> before first paint — avoids a flash.
export const bootstrapColorMode = () => {
  applyDarkClass(isDarkActive(getStoredColorMode()));
};

// Shared so the header toggle and the MUI theme in App.js stay in sync.
export const ColorModeContext = createContext({
  mode: 'auto',
  isDark: false,
  setMode: () => {},
});
